import { FormattedMessage, useIntl } from "react-intl";
import { CalendarDays, Plus } from "lucide-react";

import { SocialPostPreviewCard } from "@/components/campaign/social-post-preview-card";
import {
  getCalendarApprovalLabel,
  getCalendarEventPreviewItems,
  isCalendarDayInPast,
  isCalendarPostUnapproved,
  toCalendarDateKey,
  type CalendarDayGroup,
  type CalendarEventLike,
  type CalendarPostLike,
} from "@/lib/calendar-posts";
import {
  resolveScheduledPostPreview,
  type ScheduledPostDraft,
  type ScheduledPostPreviewLogoAsset,
} from "@/lib/scheduled-post-preview";
import { cn } from "@/lib/utils";
import { Badge } from "@runcheetah/ui";

export type CalendarPostGridPost = CalendarPostLike & {
  output?: unknown;
  draft?: ScheduledPostDraft | null;
};

export type CalendarPostGridProps<TPost extends CalendarPostGridPost> = {
  groupedDays: CalendarDayGroup<TPost>[];
  profileLabel: string;
  dailyPostCapacity?: number | null;
  eventsByDay?: Map<string, CalendarEventLike[]>;
  loading?: boolean;
  errorMessage?: string | null;
  emptyFutureLabel?: string;
  emptyPastLabel?: string;
  showCreateEmptyState?: boolean;
  countScheduledOnly?: boolean;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
  onCreateForDate?: (date: Date) => void;
  onOpenPost: (post: TPost) => void;
};

const dayFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

const weekdayFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});

const calendarGridStyle = {
  gridTemplateColumns: "repeat(7, minmax(18rem, 1fr))",
  width: "max(100%, 140rem)",
};

const calendarDayCardStyle = {
  minHeight: "38rem",
};

const calendarEmptyStateStyle = {
  minHeight: "30rem",
};

export function CalendarPostGrid<TPost extends CalendarPostGridPost>({
  groupedDays,
  profileLabel,
  dailyPostCapacity = null,
  eventsByDay,
  loading = false,
  errorMessage = null,
  emptyFutureLabel,
  emptyPastLabel,
  showCreateEmptyState = false,
  countScheduledOnly = false,
  logoAssets,
  onCreateForDate,
  onOpenPost,
}: CalendarPostGridProps<TPost>) {
  const intl = useIntl();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = toCalendarDateKey(today);
  const resolvedEmptyFutureLabel =
    emptyFutureLabel ??
    intl.formatMessage({
      id: "calendar.nothingScheduled",
      defaultMessage: "Nothing scheduled",
    });
  const resolvedEmptyPastLabel =
    emptyPastLabel ??
    intl.formatMessage({
      id: "calendar.nothingScheduled",
      defaultMessage: "Nothing scheduled",
    });

  if (loading) {
    return (
      <div className="overflow-x-auto pb-4">
        <div className="grid gap-4" style={calendarGridStyle}>
          {Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="h-96 animate-pulse rounded-2xl border bg-muted/30" />
          ))}
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="border-y py-12 text-sm text-destructive">
        <FormattedMessage
          id="calendar.loadFailed"
          defaultMessage="Failed to load calendar: {message}"
          values={{ message: errorMessage }}
        />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-4">
      <div className="grid gap-4" style={calendarGridStyle}>
        {groupedDays.map((day) => {
          const isToday = day.key === todayKey;
          const isPastDay = isCalendarDayInPast(day.date, today);
          const dayEvents = eventsByDay?.get(day.key) ?? [];
          const dayScheduledCount = day.groups.reduce(
            (sum, group) =>
              sum + group.posts.filter((post) => !isCalendarPostUnapproved(post)).length,
            0,
          );
          const dayPostCount = day.groups.reduce((sum, group) => sum + group.posts.length, 0);
          const badgeCount = countScheduledOnly ? dayScheduledCount : dayPostCount;
          const isAtDailyCapacity =
            dailyPostCapacity !== null && dayScheduledCount >= dailyPostCapacity;

          return (
            <section
              key={day.key}
              aria-current={isToday ? "date" : undefined}
              style={calendarDayCardStyle}
              className={cn(
                "relative min-h-152 min-w-0 space-y-4 overflow-hidden rounded-2xl border bg-background px-3 py-3 transition-colors",
                isToday && "border-primary/40 bg-primary/5 ring-1 ring-primary/30",
              )}
            >
              {isToday ? (
                <span className="absolute inset-x-0 top-0 h-1 bg-primary" aria-hidden="true" />
              ) : null}
              <div className="flex items-start justify-between gap-3 border-b pb-3">
                <div className="min-w-0 flex-1">
                  <h2 className="flex flex-wrap items-center gap-1.5">
                    <span
                      className={cn(
                        "inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-sm font-semibold",
                        isToday ? "bg-primary text-primary-foreground" : "text-foreground",
                      )}
                    >
                      {dayFormatter.format(day.date)}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      {weekdayFormatter.format(day.date)}
                    </span>
                    {isToday ? (
                      <Badge
                        shape="pill"
                        variant="outline"
                        className="border-primary/30 bg-primary/10 text-[11px] font-semibold text-primary"
                      >
                        <FormattedMessage id="calendar.today" defaultMessage="Today" />
                      </Badge>
                    ) : null}
                  </h2>
                  {dayEvents.length > 0 ? <CalendarEventList events={dayEvents} /> : null}
                </div>
                <Badge
                  shape="pill"
                  variant="secondary"
                  className={cn(isAtDailyCapacity && "bg-brand text-brand-foreground")}
                  aria-label={
                    dailyPostCapacity === null
                      ? intl.formatMessage(
                          {
                            id: "calendar.dayScheduledCount",
                            defaultMessage: "{count} posts",
                          },
                          { count: badgeCount },
                        )
                      : intl.formatMessage(
                          {
                            id: "calendar.dayScheduledCapacity",
                            defaultMessage: "{scheduled} of {capacity} posts scheduled",
                          },
                          {
                            scheduled: dayScheduledCount,
                            capacity: dailyPostCapacity,
                          },
                        )
                  }
                >
                  {dailyPostCapacity === null
                    ? badgeCount
                    : `${dayScheduledCount} / ${dailyPostCapacity}`}
                </Badge>
              </div>

              {day.groups.length === 0 ? (
                isPastDay || !showCreateEmptyState || !onCreateForDate ? (
                  <div
                    className="flex min-h-80 items-center justify-center p-6 text-center"
                    style={calendarEmptyStateStyle}
                  >
                    <span className="text-sm text-muted-foreground">
                      {isPastDay ? resolvedEmptyPastLabel : resolvedEmptyFutureLabel}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="flex min-h-80 w-full cursor-pointer items-center justify-center p-6 text-left transition hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    style={calendarEmptyStateStyle}
                    onClick={() => onCreateForDate(day.date)}
                    aria-label={intl.formatMessage({
                      id: "calendar.generateContent",
                      defaultMessage: "Generate content",
                    })}
                  >
                    <span className="flex min-h-40 w-full max-w-xs flex-col items-center justify-center gap-3 rounded-2xl bg-muted/40 px-6 py-7 text-center transition hover:bg-muted/60">
                      <span className="flex size-11 items-center justify-center rounded-2xl bg-brand text-brand-foreground shadow-[0_16px_40px_-24px_rgba(200,255,0,0.95)]">
                        <Plus className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="text-sm font-semibold text-foreground">
                        {dailyPostCapacity === null ? (
                          <FormattedMessage
                            id="calendar.dayScheduledCount"
                            defaultMessage="{count} posts scheduled"
                            values={{ count: dayScheduledCount }}
                          />
                        ) : (
                          <FormattedMessage
                            id="calendar.dayScheduledCapacityShort"
                            defaultMessage="{scheduled} / {capacity} scheduled"
                            values={{
                              scheduled: dayScheduledCount,
                              capacity: dailyPostCapacity,
                            }}
                          />
                        )}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        <FormattedMessage
                          id="calendar.generateContent"
                          defaultMessage="Generate content"
                        />
                      </span>
                    </span>
                  </button>
                )
              ) : (
                <div className="space-y-5">
                  {day.groups.map((group) => (
                    <div key={group.key} className="space-y-3">
                      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/60">
                        <span className="h-px flex-1 bg-border" aria-hidden="true" />
                        {group.label}
                        <span className="h-px flex-1 bg-border" aria-hidden="true" />
                      </div>
                      {group.posts.map((post) => (
                        <CalendarPostCard
                          key={post.id}
                          post={post}
                          timeLabel={timeFormatter.format(new Date(post.scheduledFor))}
                          profileLabel={profileLabel}
                          logoAssets={logoAssets}
                          onOpen={() => onOpenPost(post)}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

const getCalendarEventLabel = (event: CalendarEventLike): string =>
  event.localName !== event.name ? `${event.name} (${event.localName})` : event.name;

function CalendarEventList({ events }: { events: CalendarEventLike[] }) {
  const intl = useIntl();
  const { visibleEvents, hiddenCount } = getCalendarEventPreviewItems(events);

  return (
    <div className="mt-3 rounded-lg border bg-muted/25 px-2.5 py-2">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
        <CalendarDays className="h-3 w-3" aria-hidden="true" />
        <FormattedMessage id="calendar.events.marketEvents" defaultMessage="Market events" />
      </div>
      <div className="mt-1.5 space-y-1">
        {visibleEvents.map((event) => {
          const label = getCalendarEventLabel(event);

          return (
            <div key={event.id} className="flex min-w-0 items-center gap-2 text-xs">
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate font-medium" title={label}>
                {label}
              </span>
              <span
                className="shrink-0 rounded-full border bg-background px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground"
                title={event.countryCode}
              >
                {event.countryCode}
              </span>
            </div>
          );
        })}
      </div>
      {hiddenCount > 0 ? (
        <Badge
          shape="pill"
          variant="outline"
          className="mt-2 border-primary/20 bg-primary/10 text-[11px]"
          aria-label={intl.formatMessage(
            {
              id: "calendar.events.moreLabel",
              defaultMessage: "{count} more market events",
            },
            { count: hiddenCount },
          )}
        >
          <FormattedMessage
            id="calendar.events.more"
            defaultMessage="+{count}"
            values={{ count: hiddenCount }}
          />
        </Badge>
      ) : null}
    </div>
  );
}

function CalendarPostCard<TPost extends CalendarPostGridPost>({
  post,
  timeLabel,
  profileLabel,
  logoAssets,
  onOpen,
}: {
  post: TPost;
  timeLabel: string;
  profileLabel: string;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
  onOpen: () => void;
}) {
  const intl = useIntl();
  const preview = resolveScheduledPostPreview({
    output: post.output,
    draft: post.draft ?? null,
    logoAssets,
  });
  const unapproved = isCalendarPostUnapproved(post);
  const approvalLabel = getCalendarApprovalLabel(post, intl);

  return (
    <SocialPostPreviewCard
      preview={preview}
      timeLabel={timeLabel}
      profileLabel={profileLabel}
      approvalLabel={approvalLabel}
      showApprovalBadge={unapproved}
      muted={unapproved}
      onOpen={onOpen}
    />
  );
}
